<script setup>
import {
  ref,
  computed,
} from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import {KkBox, KkButton, KkValidationError, KkSplitButton} from '../../../base'
import {KkInfo} from '../../../ui'
import DatePicker from '../../../DatePicker'
import moment from 'moment'
import Autocomplete from '../../../Autocomplete'

const store = useStore()
const { t } = useI18n()

const props = defineProps({
  needApproval: {
    type: Boolean,
    default: false
  },
  valid: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['open-action-modal', 'cancel-alert'])

const reservationDate = ref(new Date())
const reservationHours = ref('00')
const reservationMinutes = ref('00')

const reservationDateTime = computed(() => store.getters['companiesStoreSnsPosts/reservationDateTime'])
const currentPostTiming = computed(() => store.getters['companiesStoreSnsPosts/postTiming'])
const showOnlyOwnerAuthority = computed(() => store.getters['companiesAccessibleApi/showOnlyOwnerAuthority'])

const updateSubmitActionModalType = (action) => store.dispatch('companiesStoreSnsPosts/updateSubmitActionModalType', action)
const updatePostTiming = (postTiming) => store.dispatch('companiesStoreSnsPosts/updatePostTiming', postTiming)
const updateReservationDateTime = (payload) => store.dispatch('companiesStoreSnsPosts/updateReservationDateTime',  payload)
const postTimingOptions = [
  { key: 'reserve', name: t('pages.CompaniesStorePost.postTimingOption.reserve') },
  { key: 'immediate', name: t('pages.CompaniesStorePost.postTimingOption.immediate') }
]

const isAvailableImmediatePost = computed(() => {
  return showOnlyOwnerAuthority.value || !props.needApproval
})
const disabledDates = computed(() => {
  return {
    from: moment(),
    to: moment().add(6, 'month'),
  }
})
const hoursList = computed(() => {
  const hours = []
  for (let step = 0; step < 24; step++) {
    hours.push(('00' + step).slice(-2))
  }
  return hours
})
const minutesList = computed(() => {
  const minutes = []
  for (let step = 0; step < 6; step++) {
    minutes.push((step + '00').slice(0, 2))
  }
  return minutes
})
const reservationTime = computed(() => {
  return `${reservationHours.value}:${reservationMinutes.value}`
})
const calculateReservationDateTime = computed(() => {
  if (!reservationDate.value) return null

  let reservationDateTime
  const time = reservationTime.value.split(':')
  reservationDateTime = new Date(
    reservationDate.value.getFullYear(),
    reservationDate.value.getMonth(),
    reservationDate.value.getDate(),
    Number(time[0]),
    Number(time[1]),
  )
  return reservationDateTime
})

const reservationDateTimeError = () => {
  const now = new Date
  if (!calculateReservationDateTime.value) {
    return t('pages.CompaniesStorePost.reservationDateTimeRequired')
  }
  if (calculateReservationDateTime.value.getTime() < now.getTime()) {
    return t('pages.CompaniesStorePost.FutureReservationDateTime')
  }
}
const changeReservationDate = (date) => {
  reservationDate.value = date
  updateReservationDateTime(calculateReservationDateTime.value)
}
const changeReservationHours = (hours) => {
  reservationHours.value = hours
  updateReservationDateTime(calculateReservationDateTime.value)
}
const changeReservationMinutes = (minutes) => {
  reservationMinutes.value = minutes
  updateReservationDateTime(calculateReservationDateTime.value)
}
const openActionModal = (isDraftedPost) => {
  let action = 'draft'

  if (!isDraftedPost) {
    if (currentPostTiming.value === 'reserve' && reservationDateTimeError()) {
      return
    }
    action = props.needApproval ? 'request' : 'post'
  }

  updateSubmitActionModalType(action)
  emit('open-action-modal')
}
const cancelAlert = () => {
  emit('cancel-alert')
}
const handleSelectOption = (selectedOption) => {
  updatePostTiming(selectedOption.key)
}

const load = () => {
  if (reservationDateTime.value) {
    reservationDate.value = reservationDateTime.value
    reservationHours.value = moment(reservationDateTime.value).format('HH')
    reservationMinutes.value = moment(reservationDateTime.value).format('mm')
  }

  updateReservationDateTime(calculateReservationDateTime.value)
}
load()
</script>

<template>
  <KkBox
    tag="div"
    border
  >
    <template #content>
      <h3>{{ t('pages.CompaniesStorePost.postSettings') }}</h3>
      <template v-if="currentPostTiming === 'reserve'">
        <KkInfo v-if="needApproval" alert>
          {{ t('pages.CompaniesStorePost.cautionReservationDescription') }}
        </KkInfo>

        <h4>{{ t('pages.CompaniesStorePost.reservationDate') }}</h4>
        <DatePicker
          :initial-dates="reservationDate"
          :is-single="true"
          :disabled-dates="disabledDates"
          :is-display-day-of-week="true"
          @input="changeReservationDate"
        />

        <h4>{{ t('pages.CompaniesStorePost.reservationTime') }}</h4>
        <div class="reservationTime">
          <div class="reservationTimeHours">
            <Autocomplete
              id="reservationHours"
              :data-list="hoursList"
              :value="reservationHours"
              :searchable="false"
              placeholder="00"
              @change="changeReservationHours"
            />
          </div>
          <div class="reservationTimeHourAndMinuteSeparator">:</div>
          <div class="reservationTimeMinutes">
            <Autocomplete
              id="reservationMinutes"
              :data-list="minutesList"
              :value="reservationMinutes"
              :searchable="false"
              placeholder="00"
              @change="changeReservationMinutes"
            />
          </div>
        </div>
        <KkValidationError :text="reservationDateTimeError()" />
      </template>

      <div class="buttonGroup">
        <div class="buttonGroup__item">
          <KkButton
            color="secondary"
            outlined
            :text="t('labels.cancel')"
            @click="cancelAlert()"
          />
        </div>
        <div class="buttonGroup__item">
          <KkButton
            color="secondary"
            :text="t('pages.CompaniesStorePost.draftedPost')"
            @click="openActionModal(true)"
          />
        </div>
        <div class="buttonGroup__item">
          <KkSplitButton
            v-if="isAvailableImmediatePost"
            :current-option="postTimingOptions.find(option => option.key === currentPostTiming)"
            :options="postTimingOptions"
            @select-option="handleSelectOption"
            @click="openActionModal(false)"
          >
            <template #option="slotProps">
              {{ slotProps.option.name }}
            </template>
          </KkSplitButton>
          <KkButton
            v-else
            color="primary"
            :disabled="!!reservationDateTimeError() || !props.valid"
            :text="needApproval ? t('pages.CompaniesStorePost.sendApproval') : t('pages.CompaniesStorePost.makeReservation')"
            @click="openActionModal(false)"
          />
        </div>
      </div>
    </template>
  </KkBox>
</template>

<style scoped lang="scss">
.reservationTime {
  display: flex;
  align-items: center;

  .reservationTimeHours {
    width: 45%;
  }

  .reservationTimeHourAndMinuteSeparator {
    width: 10%;
    text-align: center;
  }

  .reservationTimeMinutes {
    width: 45%;
  }
}

.repeatingPost {
  margin-top: 20px;
  margin-bottom: 20px;

  &__config {
    margin-top: 10px;
    margin-bottom: 10px;
  }
}

.buttonGroup {
  display: flex;
  flex-wrap: wrap;

  &__item {
    margin: 8px;

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  }
}
:deep(.info__text) {
  white-space: unset;
}
</style>
